/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 5: Spider Web (Influence Weights)
 * 
 * Design matched with Content Studio V2:
 * - Radar chart visualization
 * - Preset buttons (Balanced, Sales Push, Brand Build, Viral Test)
 * - Auto-save with debounce
 * - Compact sliders
 */

import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import SpiderRadar from '../../contentV2/components/SpiderRadar';

// Default to Sales Push preset
const DEFAULT_WEIGHTS = {
  product_core: 80,
  campaign_direction: 45,
  promotion: 75,
  ambassador_tone: 45,
  company_values: 45,
  compliance_guard: 60,
  viral_heuristics: 45,
  external_trend: 45
};

// Presets matching Content Studio V2
const PRESETS = {
  balanced: {
    name: 'Balanced',
    icon: '⚖️',
    values: {
      product_core: 50,
      campaign_direction: 50,
      promotion: 50,
      ambassador_tone: 50,
      company_values: 50,
      compliance_guard: 50,
      viral_heuristics: 50,
      external_trend: 50
    }
  },
  salesPush: {
    name: 'Sales Push',
    icon: '💰',
    values: {
      product_core: 80,
      campaign_direction: 45,
      promotion: 75,
      ambassador_tone: 45,
      company_values: 45,
      compliance_guard: 60,
      viral_heuristics: 45,
      external_trend: 45
    }
  },
  brandBuild: {
    name: 'Brand Build',
    icon: '🎨',
    values: {
      product_core: 45,
      campaign_direction: 70,
      promotion: 45,
      ambassador_tone: 70,
      company_values: 80,
      compliance_guard: 45,
      viral_heuristics: 45,
      external_trend: 45
    }
  },
  viralTest: {
    name: 'Viral Test',
    icon: '🔥',
    values: {
      product_core: 60,
      campaign_direction: 45,
      promotion: 45,
      ambassador_tone: 45,
      company_values: 45,
      compliance_guard: 45,
      viral_heuristics: 80,
      external_trend: 70
    }
  }
};

// Weight metadata
const WEIGHT_META = {
  product_core: {
    label: { en: 'Product Core', ms: 'Teras Produk', bm: 'Teras Produk', urban: 'Product Core' },
    shortLabel: 'Product',
    tooltip: 'Primary product/service focus. Higher = more USP emphasis.',
    icon: '🏷️'
  },
  campaign_direction: {
    label: { en: 'Campaign Hook', ms: 'Hook Kempen', bm: 'Hook Kempen', urban: 'Campaign Hook' },
    shortLabel: 'Direction',
    tooltip: 'Alignment with campaign strategy and positioning.',
    icon: '🎣'
  },
  promotion: {
    label: { en: 'Promotion', ms: 'Promosi', bm: 'Promosi', urban: 'Promotion' },
    shortLabel: 'Promo',
    tooltip: 'Promotional offers. Higher = stronger CTAs.',
    icon: '🎁'
  },
  ambassador_tone: {
    label: { en: 'Ambassadors', ms: 'Ambassador', bm: 'Ambassador', urban: 'Ambassadors' },
    shortLabel: 'Ambassador',
    tooltip: 'Brand ambassador personality influence.',
    icon: '⭐'
  },
  company_values: {
    label: { en: 'Company Values', ms: 'Nilai Syarikat', bm: 'Nilai Syarikat', urban: 'Company Values' },
    shortLabel: 'Values',
    tooltip: 'Core values & mission. Higher = deeper emotional connection.',
    icon: '💎'
  },
  compliance_guard: {
    label: { en: 'Compliance Guard', ms: 'Kawalan Pematuhan', bm: 'Kawalan Pematuhan', urban: 'Compliance Guard' },
    shortLabel: 'Compliance',
    tooltip: 'Regulatory strictness. Higher = more cautious filters.',
    icon: '🛡️'
  },
  viral_heuristics: {
    label: { en: 'Viral Heuristics', ms: 'Heuristik Viral', bm: 'Heuristik Viral', urban: 'Viral Heuristics' },
    shortLabel: 'Viral',
    tooltip: 'Viral potential optimization for engagement.',
    icon: '🔥'
  },
  external_trend: {
    label: { en: 'External Trend', ms: 'Trend Luar', bm: 'Trend Luar', urban: 'External Trend' },
    shortLabel: 'Trend',
    tooltip: 'Market trends and timely topics incorporation.',
    icon: '📈'
  }
};

const Step5SpiderWeb = ({ sessionId, sessionData, onComplete, onBack, setError }) => {
  const { language } = useLanguage();
  const [weights, setWeights] = useState(sessionData?.weights || DEFAULT_WEIGHTS);
  const [activePreset, setActivePreset] = useState(sessionData?.weights ? null : 'salesPush'); // Default to Sales Push
  const [saveStatus, setSaveStatus] = useState('');

  // Get label for current language
  const getLabel = (key) => {
    const meta = WEIGHT_META[key];
    if (!meta) return key;
    return meta.label[language] || meta.label.en || key;
  };

  // Handle slider change
  const handleSliderChange = (key, value) => {
    const newWeights = {
      ...weights,
      [key]: parseInt(value)
    };
    setWeights(newWeights);
    setActivePreset(null); // Clear preset when manually changed
    
    // Show save indicator
    setSaveStatus('saving');
    setTimeout(() => setSaveStatus('saved'), 300);
    setTimeout(() => setSaveStatus(''), 2000);
  };

  // Apply preset
  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (preset) {
      setWeights(preset.values);
      setActivePreset(presetKey);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  // Reset to default
  const resetToDefault = () => {
    setWeights(DEFAULT_WEIGHTS);
    setActivePreset('balanced');
    setSaveStatus('');
  };

  // Continue to next step
  const handleContinue = () => {
    setError(null);
    onComplete({ weights });
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.lg, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          🕸️ Spider Web
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
          {language === 'bm' || language === 'ms' 
            ? 'Sesuaikan berat pengaruh untuk penjanaan skrip AI'
            : 'Adjust influence weights for AI script generation'}
        </p>
      </div>

      {/* Main Card */}
      <div style={{ background: '#fff', borderRadius: EMZI_RADIUS.md, padding: '16px' }}>
        
        {/* Save Status */}
        {saveStatus && (
          <div style={{
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            marginBottom: '12px',
            background: saveStatus === 'saved' ? '#E8F5E9' : '#FFF4E6',
            color: saveStatus === 'saved' ? '#2E7D32' : '#F7941E',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {saveStatus === 'saved' && <span>✓</span>}
            {saveStatus === 'saving' && <span>⟳</span>}
            {saveStatus === 'saved' ? 'Saved' : 'Saving...'}
          </div>
        )}

        {/* Presets */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: 500 }}>
            Quick Presets:
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {Object.keys(PRESETS).map(key => {
              const preset = PRESETS[key];
              const isActive = activePreset === key;
              return (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  style={{
                    padding: '8px 12px',
                    background: isActive ? '#0e67a2' : '#F0F0F0',
                    border: isActive ? '2px solid #0e67a2' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: isActive ? '#fff' : '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  {preset.icon} {preset.name}
                </button>
              );
            })}
            <button
              onClick={resetToDefault}
              style={{
                padding: '8px 12px',
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 500,
                color: '#666'
              }}
            >
              🔄 Reset
            </button>
          </div>
        </div>

        {/* Radar Chart */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px',
          background: '#FAFAFA',
          borderRadius: '8px'
        }}>
          <SpiderRadar 
            values={weights} 
            size={Math.min(300, window.innerWidth - 80)}
          />
          
          {/* Legend - 2 column compact */}
          <div style={{ 
            marginTop: '12px',
            width: '100%',
            maxWidth: '340px'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: '6px',
              fontSize: '11px'
            }}>
              {Object.keys(WEIGHT_META).map((key) => {
                const meta = WEIGHT_META[key];
                const value = weights[key];
                
                return (
                  <div 
                    key={key}
                    style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 8px',
                      background: '#fff',
                      borderRadius: '4px',
                      border: '1px solid #E0E0E0'
                    }}
                  >
                    <span style={{ 
                      color: '#666',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginRight: '4px'
                    }}>
                      {meta.shortLabel}
                    </span>
                    <span style={{ 
                      color: value >= 70 ? '#F7941E' : value <= 30 ? '#999' : '#0e67a2',
                      fontWeight: 700,
                      minWidth: '28px',
                      textAlign: 'right'
                    }}>
                      {value}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Weight Sliders - Compact Grid */}
        <div style={{ 
          display: 'grid', 
          gap: '10px',
          marginBottom: '12px'
        }}>
          {Object.keys(weights).map(key => {
            const meta = WEIGHT_META[key];
            const value = weights[key];
            
            return (
              <div key={key} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '10px 12px',
                background: '#FAFAFA',
                borderRadius: '8px'
              }}>
                {/* Label + Value */}
                <div style={{ flex: '0 0 120px' }}>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>{meta?.icon}</span>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {getLabel(key)}
                    </span>
                  </div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 700, 
                    color: value >= 70 ? '#F7941E' : value <= 30 ? '#999' : '#0e67a2'
                  }}>
                    {value}
                  </div>
                </div>
                
                {/* Slider */}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => handleSliderChange(key, e.target.value)}
                  title={meta?.tooltip}
                  style={{
                    flex: 1,
                    height: '8px',
                    borderRadius: '4px',
                    outline: 'none',
                    background: `linear-gradient(to right, #0e67a2 0%, #0e67a2 ${value}%, #E0E0E0 ${value}%, #E0E0E0 100%)`,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer'
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <button 
          onClick={onBack} 
          style={{ 
            flex: 1, 
            padding: '14px', 
            background: '#fff', 
            border: '2px solid #0e67a2', 
            borderRadius: '10px', 
            color: '#0e67a2', 
            fontSize: '15px', 
            fontWeight: '600', 
            cursor: 'pointer' 
          }}
        >
          ← Back
        </button>
        <button 
          onClick={handleContinue}
          style={{ 
            flex: 2, 
            padding: '14px', 
            background: '#F7941E', 
            border: 'none', 
            borderRadius: '10px', 
            color: '#fff', 
            fontSize: '15px', 
            fontWeight: '600', 
            cursor: 'pointer' 
          }}
        >
          Continue to Evidence →
        </button>
      </div>
    </div>
  );
};

export default Step5SpiderWeb;
